import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import { ChatPanel } from "./ChatPanel";

let mockImageEnabled = true;

jest.mock("@metalmind-ab/mmagent-chat-ui/styles.css", () => ({}), {
  virtual: true,
});
jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ...jest.requireActual("@metalmind-ab/mmagent-chat-ui"),
  AttachmentPicker: ({
    attachments,
    onChange,
    refusal,
    fileHandlers,
  }: {
    attachments: { fileName: string }[];
    onChange: (images: unknown[]) => void;
    refusal?: string;
    fileHandlers?: Record<string, (file: File) => Promise<unknown>>;
  }) => (
    <div>
      <span data-testid="picker-file-handlers">
        {Object.keys(fileHandlers || {}).join(",")}
      </span>
      <button
        type="button"
        onClick={() =>
          onChange([
            {
              kind: "image",
              reference: {
                attachment_id: "image-1",
                media_type: "image/png",
                size_bytes: 123,
              },
              fileName: "red.png",
              previewUrl: "blob:red",
            },
          ])
        }
      >
        Select image
      </button>
      {attachments.map((image) => (
        <span key={image.fileName}>{image.fileName}</span>
      ))}
      {refusal && <span data-testid="attachment-refusal">{refusal}</span>}
    </div>
  ),
}));
jest.mock("../../../../hooks/useAuthHeaders", () => ({
  useAuthHeaders: () => getHeaders,
}));
jest.mock("../../../../hooks/useChatImageAttachments", () => ({
  useChatImageAttachments: () => ({
    enabled: mockImageEnabled,
    uploadImage: jest.fn(),
  }),
}));
jest.mock("../../../../config/runtimeConfig", () => ({
  getApiBaseUrl: () => "http://localhost",
}));
jest.mock("../../../../utils/chatResumeOptions", () => ({
  buildChatResumeOptions: () => ({}),
}));

const getHeaders = async () => ({});
const originalFetch = global.fetch;
const originalRevoke = URL.revokeObjectURL;

type Stream = {
  push: (event: object) => void;
  aborted: () => boolean;
};

function makeStream(signal: AbortSignal): {
  stream: Stream;
  body: {
    getReader: () => {
      read: () => Promise<ReadableStreamReadResult<Uint8Array>>;
    };
  };
} {
  const queue: ReadableStreamReadResult<Uint8Array>[] = [];
  let receive: ((result: ReadableStreamReadResult<Uint8Array>) => void) | null =
    null;
  let aborted = false;
  signal.addEventListener("abort", () => {
    aborted = true;
  });
  const enqueue = (result: ReadableStreamReadResult<Uint8Array>) => {
    if (receive) {
      const resolve = receive;
      receive = null;
      resolve(result);
    } else {
      queue.push(result);
    }
  };
  return {
    stream: {
      push: (event) =>
        enqueue({
          done: false,
          value: new TextEncoder().encode(`data: ${JSON.stringify(event)}\n\n`),
        }),
      aborted: () => aborted,
    },
    body: {
      getReader: () => ({
        read: () =>
          queue.length
            ? Promise.resolve(queue.shift()!)
            : new Promise((resolve) => {
                receive = resolve;
              }),
      }),
    },
  };
}

beforeEach(() => {
  mockImageEnabled = true;
  useChatStore.getState().clearMessages();
  useChatStore.getState().setConversationId(null);
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  URL.revokeObjectURL = jest.fn();
});

it("hides the attachment control when image capability is unavailable", async () => {
  mockImageEnabled = false;
  global.fetch = jest.fn(async () => ({
    ok: true,
    status: 200,
    json: async () => ({ items: [], total: 0 }),
  })) as jest.Mock;
  render(<ChatPanel workspaceId="workspace-1" />);
  await screen.findByPlaceholderText("Ask about database, views, canvases...");
  expect(
    screen.queryByRole("button", { name: "Select image" }),
  ).not.toBeInTheDocument();
});

it("keeps the picker on its image-only default", async () => {
  global.fetch = jest.fn(async () => ({
    ok: true,
    status: 200,
    json: async () => ({ items: [], total: 0 }),
  })) as jest.Mock;
  render(<ChatPanel workspaceId="workspace-1" />);
  await screen.findByRole("button", { name: "Select image" });
  expect(screen.getByTestId("picker-file-handlers")).toBeEmptyDOMElement();
});

afterEach(() => {
  global.fetch = originalFetch;
  URL.revokeObjectURL = originalRevoke;
});

it("renders text and image streams and accepts a second send", async () => {
  const streams: Stream[] = [];
  const bodies: unknown[] = [];
  global.fetch = jest.fn(async (_url, init) => {
    if (init?.method !== "POST")
      return {
        ok: true,
        status: 200,
        json: async () => ({ items: [], total: 0 }),
      } as Response;
    bodies.push(JSON.parse(String(init.body)));
    const { stream, body } = makeStream(init.signal as AbortSignal);
    streams.push(stream);
    return {
      ok: true,
      status: 200,
      body,
      headers: { get: () => null },
    } as unknown as Response;
  });

  render(<ChatPanel workspaceId="workspace-1" />);
  const input = await screen.findByPlaceholderText(
    "Ask about database, views, canvases...",
  );
  fireEvent.change(input, { target: { value: "hello" } });
  fireEvent.keyDown(input, { key: "Enter" });
  await waitFor(() => expect(streams).toHaveLength(1));
  streams[0].push({ type: "content_delta", content: "Text reply" });
  streams[0].push({ type: "done", conversation_id: 1 });
  await screen.findByText("Text reply");
  await waitFor(() =>
    expect(
      screen.queryByRole("button", { name: "Stop generating (ESC)" }),
    ).not.toBeInTheDocument(),
  );
  expect(streams[0].aborted()).toBe(false);

  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.change(input, { target: { value: "describe this" } });
  fireEvent.keyDown(input, { key: "Enter" });
  await waitFor(() => expect(streams).toHaveLength(2));
  expect(bodies[1]).toEqual({
    content: "describe this",
    images: [
      { attachment_id: "image-1", media_type: "image/png", size_bytes: 123 },
    ],
  });
  streams[1].push({ type: "content_delta", content: "Image reply" });
  streams[1].push({ type: "done", conversation_id: 1 });
  await screen.findByText("Image reply");
  expect(URL.revokeObjectURL).not.toHaveBeenCalledWith("blob:red");
  await waitFor(() =>
    expect(
      screen.queryByRole("button", { name: "Stop generating (ESC)" }),
    ).not.toBeInTheDocument(),
  );
  expect(streams[1].aborted()).toBe(false);

  fireEvent.change(input, { target: { value: "again" } });
  fireEvent.keyDown(input, { key: "Enter" });
  await waitFor(() => expect(streams).toHaveLength(3));
});

it("settles a running tool when the done frame arrives before its tool_end", async () => {
  let stream: Stream | null = null;
  global.fetch = jest.fn(async (_url, init) => {
    if (init?.method !== "POST")
      return {
        ok: true,
        status: 200,
        json: async () => ({ items: [], total: 0 }),
      } as Response;
    const responseStream = makeStream(init.signal as AbortSignal);
    stream = responseStream.stream;
    return {
      ok: true,
      status: 200,
      body: responseStream.body,
      headers: { get: () => null },
    } as unknown as Response;
  });

  render(<ChatPanel workspaceId="workspace-1" />);
  const input = await screen.findByPlaceholderText(
    "Ask about database, views, canvases...",
  );
  fireEvent.change(input, { target: { value: "Create a view" } });
  fireEvent.keyDown(input, { key: "Enter" });
  await waitFor(() => expect(stream).not.toBeNull());

  stream!.push({
    type: "tool_start",
    tool_name: "create_view",
    tool_call_id: "tool-1",
    tool_args: {},
  });
  stream!.push({ type: "done", conversation_id: 1 });

  await waitFor(() => expect(useChatStore.getState().isLoading).toBe(false));
  const assistant = useChatStore
    .getState()
    .messages.find((message) => message.role === "assistant");
  expect(assistant?.isStreaming).toBe(false);
  expect(assistant?.toolExecutions?.[0]?.status).toBe("completed");
  expect(input).toBeEnabled();
  expect(screen.queryByText("running...")).not.toBeInTheDocument();
});

it("clears an unsent image without revoking the picker-owned preview", async () => {
  global.fetch = jest.fn(async () => ({
    ok: true,
    status: 200,
    json: async () => ({ items: [], total: 0 }),
  })) as jest.Mock;
  render(<ChatPanel workspaceId="workspace-1" />);
  await screen.findByPlaceholderText("Ask about database, views, canvases...");
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  expect(screen.getByText("red.png")).toBeInTheDocument();
  fireEvent.click(screen.getByRole("button", { name: "New conversation" }));
  expect(screen.queryByText("red.png")).not.toBeInTheDocument();
  expect(URL.revokeObjectURL).not.toHaveBeenCalledWith("blob:red");
});

it("shows a server image refusal while keeping the draft and image", async () => {
  global.fetch = jest.fn(async (_url, init) => {
    if (init?.method === "POST")
      return {
        ok: false,
        status: 400,
        json: async () => ({
          detail: {
            error_code: "IMAGE_INPUT_DISABLED",
            message: "Image input is disabled",
          },
        }),
      } as unknown as Response;
    return {
      ok: true,
      status: 200,
      json: async () => ({ items: [], total: 0 }),
    } as Response;
  });
  render(<ChatPanel workspaceId="workspace-1" />);
  const input = await screen.findByPlaceholderText(
    "Ask about database, views, canvases...",
  );
  fireEvent.click(screen.getByRole("button", { name: "Select image" }));
  fireEvent.change(input, { target: { value: "describe this" } });
  fireEvent.keyDown(input, { key: "Enter" });
  expect(await screen.findByTestId("attachment-refusal")).toHaveTextContent(
    "Image input is disabled",
  );
  expect(input).toHaveValue("describe this");
  expect(screen.getByText("red.png")).toBeInTheDocument();
});

it("clears an unsent image when the active conversation changes", async () => {
  global.fetch = jest.fn(async () => ({
    ok: true,
    status: 200,
    json: async () => ({ items: [], total: 0 }),
  })) as jest.Mock;
  render(<ChatPanel workspaceId="workspace-1" />);
  fireEvent.click(await screen.findByRole("button", { name: "Select image" }));
  expect(screen.getByText("red.png")).toBeInTheDocument();

  act(() => {
    useChatStore.getState().setConversationId(7);
  });

  expect(screen.queryByText("red.png")).not.toBeInTheDocument();
  // Preview release belongs to the picker, not the host.
  expect(URL.revokeObjectURL).not.toHaveBeenCalled();
});
